import React from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";

const addInPromptLoopVersion = "2026.06.10.8";

declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "fluent-button": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        appearance?: "accent" | "lightweight" | "neutral" | "outline" | "stealth";
      };
      "fluent-tab": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
      "fluent-tabs": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        activeid?: string;
      };
    }
  }
}

function HostView(): React.ReactElement {
  return (
    <section id="hostView" className="pane-view" aria-label="Share presentation">
      <p id="shareStatus" className="status share-status" role="status" aria-live="polite">Not sharing</p>
      <p className="intro">Start a shared timer from this PowerPoint presentation.</p>

      <section className="editor-section" aria-labelledby="shareAsTitle">
        <h2 id="shareAsTitle">Share as...</h2>
        <div id="shareSpeakerShimmer" className="shimmer-details-list share-speaker-shimmer" role="status" aria-live="polite" aria-label="Loading co-presenters">
          <div className="shimmer-row" aria-hidden="true"><span></span><span></span></div>
        </div>
        <div id="shareSpeakerPicker" className="speaker-picker share-speaker-picker share-combobox" hidden>
          <span id="shareSpeakerPickerAvatar" className="share-combobox-avatar" aria-hidden="true"></span>
          <input
            id="shareSpeakerPickerInput"
            className="share-speaker-input"
            type="text"
            role="combobox"
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded="false"
            aria-controls="shareSpeakerList"
            aria-label="Share as co-presenter" />
          <button
            id="shareSpeakerPickerButton"
            type="button"
            className="share-combobox-button"
            aria-haspopup="listbox"
            aria-expanded="false"
            aria-controls="shareSpeakerList"
            aria-label="Show co-presenters"></button>
          <div id="shareSpeakerList" className="speaker-picker-list" role="listbox" aria-label="Share as co-presenter" hidden />
        </div>
        <p id="shareSpeakersEmptyState" className="empty-state" hidden>No co-presenters were found in this presentation.</p>
        <div className="command-row">
          <button id="startSharingButton" type="button" className="primary" disabled>Start sharing</button>
        </div>
      </section>

      <section id="shareResultPanel" className="status-panel" aria-labelledby="shareAccessTitle" hidden>
        <div className="access-card">
          <div className="field">
            <label htmlFor="mainPresenterLinkOutput">Main presenter link</label>
            <div className="copy-row">
              <input id="mainPresenterLinkOutput" type="text" readOnly aria-label="Main presenter link" />
              <button id="copyMainPresenterLinkButton" type="button">Copy</button>
            </div>
          </div>
        </div>
        <div className="access-card" aria-labelledby="shareAccessTitle">
          <h2 id="shareAccessTitle">Participant access</h2>
          <div className="field">
            <label htmlFor="participantCodeOutput">Participant code</label>
            <div className="copy-row">
              <input id="participantCodeOutput" className="code-output" type="text" readOnly aria-label="Participant code" />
              <button id="copyParticipantCodeButton" type="button">Copy</button>
            </div>
          </div>
          <div className="field">
            <label htmlFor="participantLinkOutput">Participant link</label>
            <div className="copy-row">
              <input id="participantLinkOutput" type="text" readOnly aria-label="Participant link" />
              <button id="copyParticipantLinkButton" type="button">Copy</button>
            </div>
          </div>
        </div>
        <section className="access-card access-group" aria-labelledby="coPresenterAccessTitle">
          <h3 id="coPresenterAccessTitle">Co-presenter access</h3>
          <p id="coPresenterLinksStatus" className="access-summary" role="status" aria-live="polite">Co-presenter links appear after sharing starts.</p>
          <div className="link-list-header" aria-hidden="true">
            <span>Presenter</span>
            <span>Status</span>
            <span>Code</span>
            <span>Link</span>
          </div>
          <div id="speakerLinksPanel" className="link-list" aria-label="Co-presenter links" />
        </section>
      </section>
    </section>
  );
}

function ConnectView(): React.ReactElement {
  return (
    <section id="connectView" className="pane-view" aria-label="Connect to shared timer" hidden>
      <p className="intro">Join a Timerinator shared timer from this PowerPoint presentation.</p>

      <div className="field">
        <label htmlFor="shareCodeInput">Share code or speaker link</label>
        <input id="shareCodeInput" type="text" autoComplete="off" inputMode="text" />
      </div>

      <div className="command-row">
        <button id="connectButton" type="button" className="primary">Connect</button>
        <button id="disconnectButton" type="button" hidden>Disconnect</button>
      </div>
      <p id="connectStatus" className="status" role="status" aria-live="polite">Not connected</p>

      <section id="timerSection" className="status-panel" aria-labelledby="timerTitle" hidden>
        <div className="section-heading">
          <h2 id="timerTitle">Shared timer</h2>
          <span id="controlBadge" className="badge">Viewing</span>
        </div>
        <p id="presentationName" className="presentation-name">Shared timer</p>
        <p id="timerState" className="timer-state">Waiting</p>
        <p id="slideStatus" className="status">Slide sync is waiting for speaker control.</p>
        <div className="command-row">
          <button id="startRemoteButton" type="button" disabled>Start</button>
        </div>
      </section>
    </section>
  );
}

function EditPivot(): React.ReactElement {
  return (
    <fluent-tabs className="editor-pivot fluent-icon-pivot" activeid="settingsTabButton" aria-label="Edit timer sections">
      <fluent-tab id="settingsTabButton" role="tab" className="pivot-button selected" aria-controls="settingsPanel" aria-selected="true" aria-pressed="true" aria-label="Settings" title="Settings">
        <span className="pivot-icon pivot-icon-settings" aria-hidden="true" />
        <span className="pivot-text">Settings</span>
      </fluent-tab>
      <fluent-tab id="sectionsTabButton" role="tab" className="pivot-button" aria-controls="sectionsPanel" aria-selected="false" aria-pressed="false" aria-label="Sections" title="Sections">
        <span className="pivot-icon pivot-icon-sections" aria-hidden="true" />
        <span className="pivot-text">Sections</span>
      </fluent-tab>
      <fluent-tab id="speakersTabButton" role="tab" className="pivot-button" aria-controls="speakersPanel" aria-selected="false" aria-pressed="false" aria-label="Speakers" title="Speakers">
        <span className="pivot-icon pivot-icon-speakers" aria-hidden="true" />
        <span className="pivot-text">Speakers</span>
      </fluent-tab>
      <fluent-tab id="breaksTabButton" role="tab" className="pivot-button" aria-controls="breaksPanel" aria-selected="false" aria-pressed="false" aria-label="Breaks" title="Breaks">
        <span className="pivot-icon pivot-icon-breaks" aria-hidden="true" />
        <span className="pivot-text">Breaks</span>
      </fluent-tab>
    </fluent-tabs>
  );
}

function EditView(): React.ReactElement {
  return (
    <section id="editView" className="pane-view" aria-label="Edit timer" hidden>
      <EditPivot />

      <section id="settingsPanel" className="editor-page" role="tabpanel" aria-labelledby="settingsTabButton">
        <section className="editor-section" aria-labelledby="timingSettingsTitle">
          <h2 id="timingSettingsTitle">Timing</h2>
          <div className="field">
            <label htmlFor="scheduledStartInput">Scheduled start</label>
            <input id="scheduledStartInput" type="text" autoComplete="off" placeholder="hh:mm AM or hh:mm" />
          </div>
          <div className="field">
            <div className="field-label-row">
              <label htmlFor="delayedStartSlideInput">Advance to slide # on start</label>
              <span className="info-tooltip">
                <button
                  type="button"
                  className="info-tooltip-button"
                  aria-label="About delayed start slide"
                  aria-describedby="delayedStartSlideTooltip"
                >
                  i
                </button>
                <span id="delayedStartSlideTooltip" role="tooltip" className="info-tooltip-content">
                  When the timer starts, PowerPoint will advance to this slide number after the delay
                </span>
              </span>
            </div>
            <input id="delayedStartSlideInput" type="number" inputMode="numeric" min="1" step="1" />
          </div>
          <div className="field">
            <label htmlFor="startOffsetInput">Timer start position</label>
            <input id="startOffsetInput" type="text" autoComplete="off" placeholder="1h, 5m, 90s, or 00:00:38" />
          </div>
        </section>
      </section>

      <section id="sectionsPanel" className="editor-page" role="tabpanel" aria-labelledby="sectionsTabButton" hidden>
        <section className="editor-section" aria-labelledby="availableTimeTitle">
          <h2 id="availableTimeTitle">Available time</h2>
          <div className="available-time-row">
            <label htmlFor="totalAvailableInput">Total available</label>
            <input id="totalAvailableInput" type="text" autoComplete="off" placeholder="1h, 5m, 90s, or 00:00:38" />
          </div>
          <dl className="time-summary">
            <div><dt>Breaks</dt><dd id="breakTotalOutput">00:00</dd></div>
            <div><dt>Slide time</dt><dd id="slideTimeOutput">00:00</dd></div>
            <div><dt>Allocated</dt><dd id="allocatedTimeOutput">00:00</dd></div>
            <div><dt>Remaining</dt><dd id="remainingTimeOutput">00:00</dd></div>
          </dl>
          <p id="overAllocatedWarning" className="warning" hidden>Allocated exceeds available time.</p>
        </section>
        <section className="editor-section" aria-labelledby="sectionsTitle">
          <div className="section-heading">
            <h2 id="sectionsTitle">Sections</h2>
          </div>
          <div id="sectionsShimmer" className="fluent-details-list compact shimmer-details-list" role="status" aria-live="polite" aria-label="Loading sections">
            <div className="shimmer-row" aria-hidden="true"><span></span><span></span></div>
            <div className="shimmer-row" aria-hidden="true"><span></span><span></span></div>
            <div className="shimmer-row" aria-hidden="true"><span></span><span></span></div>
          </div>
          <div id="sectionsList" className="editor-list fluent-details-list compact" role="grid" aria-label="Sections" />
          <p id="sectionsEmptyState" className="empty-state">No PowerPoint sections were found in this presentation.</p>
        </section>
      </section>

      <section id="speakersPanel" className="editor-page" role="tabpanel" aria-labelledby="speakersTabButton" hidden>
        <section className="editor-section" aria-labelledby="speakersTitle">
          <div className="section-heading">
            <h2 id="speakersTitle">Speakers</h2>
          </div>
          <div className="fluent-commandbar" role="toolbar" aria-label="Speaker commands">
            <fluent-button id="addSpeakerButton" appearance="stealth" className="commandbar-button">
              <span className="commandbar-icon commandbar-icon-add" aria-hidden="true" />
              <span>New speaker</span>
            </fluent-button>
          </div>
          <div id="speakersShimmer" className="fluent-details-list compact shimmer-details-list" role="status" aria-live="polite" aria-label="Loading speakers">
            <div className="shimmer-row" aria-hidden="true"><span></span><span></span></div>
            <div className="shimmer-row" aria-hidden="true"><span></span><span></span></div>
            <div className="shimmer-row" aria-hidden="true"><span></span><span></span></div>
          </div>
          <div id="speakersList" className="editor-list fluent-details-list compact" role="grid" aria-label="Speakers" />
          <p id="speakersEmptyState" className="empty-state">Speakers help Timerinator show who is presenting now and who is coming next.</p>
        </section>
      </section>

      <section id="breaksPanel" className="editor-page" role="tabpanel" aria-labelledby="breaksTabButton" hidden>
        <section className="editor-section" aria-labelledby="breaksTitle">
          <div className="section-heading">
            <h2 id="breaksTitle">Breaks</h2>
          </div>
          <div className="fluent-commandbar" role="toolbar" aria-label="Break commands">
            <fluent-button id="addBreakButton" appearance="stealth" className="commandbar-button">
              <span className="commandbar-icon commandbar-icon-add" aria-hidden="true" />
              <span>New break</span>
            </fluent-button>
          </div>
          <div id="breaksShimmer" className="fluent-details-list compact shimmer-details-list" role="status" aria-live="polite" aria-label="Loading breaks">
            <div className="shimmer-row" aria-hidden="true"><span></span><span></span></div>
            <div className="shimmer-row" aria-hidden="true"><span></span><span></span></div>
            <div className="shimmer-row" aria-hidden="true"><span></span><span></span></div>
          </div>
          <div id="breaksList" className="editor-list fluent-details-list compact" role="grid" aria-label="Breaks" />
          <p id="breaksEmptyState" className="empty-state">Breaks are pauses in your timer that don't count against section time.</p>
        </section>
      </section>

      <p id="editStatus" className="status" role="status" aria-live="polite" hidden />
    </section>
  );
}

function TaskpaneShell(): React.ReactElement {
  return (
    <main className="pane" aria-label="Timerinator PowerPoint add-in">
      <p className="addin-version" aria-label={`PowerPoint add-in build ${addInPromptLoopVersion}`}>
        PowerPoint add-in build {addInPromptLoopVersion}
      </p>
      <HostView />
      <ConnectView />
      <EditView />
    </main>
  );
}

export function renderTaskpaneShell(): void {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Task pane root element was not found.");
  }

  const root = createRoot(rootElement);
  flushSync(() => {
    root.render(<TaskpaneShell />);
  });
}
